import type { StoreApi } from "zustand";
import { applyWorkerActuatorCtrlSnapshotToStores } from "../../physics/actuatorMapping";
import { setPhysicsWorker } from "../../physics/workerHandle";
import { addBreadcrumb } from "../../monitoring/sentry";
import { getSceneObservabilitySummary } from "../../monitoring/sceneObservability";
import { reportRuntimeError } from "../../monitoring/runtimeErrors";
import { physicsUserErrorToast } from "./physicsUserErrors";
import { logger } from "../../utils/logger";
import { getSceneState } from "../sceneStore";
import type { BodyPoseBuffer, PhysicsState } from "../physicsStore";

type DiagnosticLevel = "info" | "warn" | "error";
type SensorMeta = { index: number; name: string; adr: number; dim: number };
type WorkerPayload = {
  sensors?: SensorMeta[] | null;
  engine?: PhysicsState["engine"];
  level?: DiagnosticLevel | "err";
  msg?: string;
  status?: unknown;
  ready?: unknown;
  message?: string;
  stage?: string;
  source?: string;
  data?: unknown;
  timestamp?: number;
  bodyNames?: string[];
  poses?: BodyPoseBuffer;
  poseStride?: number;
  simStepCount?: number;
  running?: boolean;
  paused?: boolean;
  mapped?: number;
  total?: number;
  error?: string;
  lastControlsSummary?: unknown;
  controls?: Record<string, number>;
  details?: unknown;
};

type WorkerMessage = {
  type?: string;
  payload?: WorkerPayload;
  error?: unknown;
};

type PhysicsDebugWindow = Window & {
  __lastPhysicsSensorDiagnostics?: unknown;
  __debugExternalForces?: unknown;
};

type WorkerMessageHandlerOptions = {
  worker: Worker;
  get: StoreApi<PhysicsState>["getState"];
  set: StoreApi<PhysicsState>["setState"];
  onReady: (msg: WorkerMessage) => void;
  onWorkerProgress?: (payload?: WorkerPayload) => void;
  clearWorkerWatchdog?: (worker?: Worker | null) => void;
  clearPendingHelloTimeout: (worker?: Worker | null) => void;
  devLog: (...args: unknown[]) => void;
};

const physicsErrorToastDetail = {
  tone: "warning",
  action: {
    label: "here.",
    event: "physics-error-send-feedback",
  },
  durationMs: 12000,
};

export const createPhysicsWorkerMessageHandler = ({
  worker,
  get,
  set,
  onReady,
  onWorkerProgress,
  clearWorkerWatchdog,
  clearPendingHelloTimeout,
  devLog,
}: WorkerMessageHandlerOptions) => {
  let lastTs = performance.now();
  let stepCount = 0;

  return (ev: MessageEvent) => {
    const msg = ev.data as WorkerMessage;
    if (!msg || !msg.type) return;

    switch (msg.type) {
      case "worker-loaded": {
        devLog("[physics] Worker loaded successfully:", msg.payload);
        devLog("[physics] Sending hello message to worker");
        worker.postMessage({ type: "hello" });
        break;
      }
      case "ready": {
        onReady(msg);
        break;
      }
      case "mjlog": {
        const { level, msg: text } = msg.payload || {};
        if (level === "err") {
          logger.error("[mujoco-worker]", text);
          get().actions.addDiagnostic({
            level: "error",
            message: text || "",
            source: "worker",
            stage: "module-load",
          });
        } else {
          devLog("[mujoco-worker]", text);
        }
        break;
      }
      case "initialized": {
        devLog("[physics] Worker initialized successfully");
        const currentState = get();
        addBreadcrumb("physics_worker_initialized", {
          area: "physics",
          action: "worker.initialized",
          operation: "physics-run",
          phase: "worker-initialized",
          engine: currentState.engine,
          loadMode: currentState.loadMode,
          mappedBodies: currentState.mappedBodies,
          totalBodies: currentState.totalBodies,
          ...getSceneObservabilitySummary(getSceneState()),
        });
        devLog(
          "[Physics] Physics now running, initialJointPositions saved:",
          !!currentState.initialJointPositions
        );
        setPhysicsWorker(worker);
        set({ status: "running", startupStartedAt: null, isRunning: true });
        onWorkerProgress?.({ simStepCount: 0, running: true, paused: false });
        break;
      }
      case "heartbeat": {
        onWorkerProgress?.(msg.payload);
        break;
      }
      case "sensorMeta": {
        try {
          set({ sensorMeta: msg.payload?.sensors ?? null });
          window.dispatchEvent(new CustomEvent("physics-sensor-meta", { detail: msg.payload }));
        } catch (error) {
          logger.warn("[Physics] Failed to dispatch physics-sensor-meta event", error);
        }
        break;
      }
      case "sensorDiagnostics": {
        try {
          (window as PhysicsDebugWindow).__lastPhysicsSensorDiagnostics = msg.payload || null;
          window.dispatchEvent(
            new CustomEvent("physics-sensor-diagnostics", { detail: msg.payload })
          );
        } catch (error) {
          logger.warn("[Physics] Failed to dispatch physics-sensor-diagnostics event", error);
        }
        break;
      }
      case "postInitActuatorControls": {
        try {
          const controls = msg.payload?.controls;
          if (!controls || typeof controls !== "object") break;
          const sceneState = getSceneState();
          const { positionTargets, motorControls } = applyWorkerActuatorCtrlSnapshotToStores(
            controls,
            sceneState
          );
          const hasPos = Object.keys(positionTargets).length > 0;
          const hasMot = Object.keys(motorControls).length > 0;
          if (!hasPos && !hasMot) break;
          set((state) => ({
            actuatorPositionTargets: hasPos
              ? { ...state.actuatorPositionTargets, ...positionTargets }
              : state.actuatorPositionTargets,
            actuatorMotorControls: hasMot
              ? { ...state.actuatorMotorControls, ...motorControls }
              : state.actuatorMotorControls,
          }));
        } catch (error) {
          logger.warn("[Physics] Failed to apply post-init actuator controls", error);
        }
        break;
      }
      case "initStatus": {
        const { status, ready } = msg.payload || {};
        devLog("[physics] Init status:", status, "Ready:", ready);
        if (ready) {
          devLog("[physics] All initialization steps complete");
        }
        break;
      }
      case "engine": {
        set({ engine: msg.payload?.engine || "unknown" });
        addBreadcrumb("physics_engine_selected", {
          area: "physics",
          action: "worker.engine",
          operation: "physics-run",
          phase: "engine-selected",
          engine: msg.payload?.engine || "unknown",
        });
        devLog("[physics] engine:", msg.payload);
        break;
      }
      case "diagnostic": {
        const payload = msg.payload || {};
        try {
          get().actions.addDiagnostic({
            level: payload.level === "err" ? "error" : payload.level || "info",
            message: payload.message || "",
            stage: payload.stage,
            source: payload.source || "worker",
            data: payload.data,
            timestamp: payload.timestamp,
          });
        } catch (error) {
          logger.warn("[Physics] Failed to add diagnostic", error);
        }
        break;
      }
      case "poses": {
        onWorkerProgress?.(msg.payload);
        try {
          window.dispatchEvent(new CustomEvent("physics-poses", { detail: msg.payload }));
        } catch (error) {
          logger.warn("[Physics] Failed to dispatch physics-poses event", error);
        }

        try {
          const prev = get().latestPoses;
          const version = (prev?.version || 0) + 1;
          const bodyNames = msg.payload?.bodyNames || [];
          const poses = msg.payload?.poses || [];
          set({ latestPoses: { bodyNames, poses, version } });
        } catch (error) {
          logger.warn("[Physics] Failed to update latest poses", error);
        }

        stepCount += 1;
        const now = performance.now();
        if (now - lastTs >= 1000) {
          set({ lastStepHz: stepCount });
          stepCount = 0;
          lastTs = now;
        }
        break;
      }
      case "recordingInfo": {
        try {
          window.dispatchEvent(new CustomEvent("physics-recording-info", { detail: msg.payload }));
        } catch (error) {
          logger.warn("[Physics] Failed to dispatch physics-recording-info event", error);
        }
        break;
      }
      case "recordStep": {
        try {
          window.dispatchEvent(new CustomEvent("physics-record-step", { detail: msg.payload }));
        } catch (error) {
          logger.warn("[Physics] Failed to dispatch physics-record-step event", error);
        }
        break;
      }
      case "externalForcePulseEvent": {
        try {
          try {
            if (
              typeof window !== "undefined" &&
              (window as PhysicsDebugWindow).__debugExternalForces === true
            ) {
              devLog("[Physics] externalForcePulseEvent received", msg.payload);
            }
          } catch {
            /* intentionally empty */
          }
          window.dispatchEvent(
            new CustomEvent("physics-external-force-pulse", { detail: msg.payload })
          );
        } catch (error) {
          logger.warn("[Physics] Failed to dispatch physics-external-force-pulse event", error);
        }
        break;
      }
      case "actuatorControlPulseEvent": {
        try {
          window.dispatchEvent(
            new CustomEvent("physics-actuator-control-pulse", { detail: msg.payload })
          );
        } catch (error) {
          logger.warn("[Physics] Failed to dispatch physics-actuator-control-pulse event", error);
        }
        break;
      }
      case "state": {
        try {
          window.dispatchEvent(new CustomEvent("physics-state", { detail: msg.payload }));
        } catch (error) {
          logger.warn("[Physics] Failed to dispatch physics-state event", error);
        }
        break;
      }
      case "freeJointPoseSet": {
        try {
          window.dispatchEvent(new CustomEvent("physics-freejoint-pose", { detail: msg.payload }));
        } catch (error) {
          logger.warn("[Physics] Failed to dispatch physics-freejoint-pose event", error);
        }
        break;
      }
      case "mapping": {
        const mapped = Number(msg.payload?.mapped) || 0;
        const total = Number(msg.payload?.total) || 0;
        set({ mappedBodies: mapped, totalBodies: total });
        addBreadcrumb("physics_mapping_updated", {
          area: "physics",
          action: "worker.mapping",
          operation: "physics-run",
          phase: "mapping-updated",
          mappedBodies: mapped,
          totalBodies: total,
        });
        break;
      }
      case "debug": {
        set({ debugInfo: msg.payload });
        try {
          const summary = msg.payload?.lastControlsSummary;
          if (summary) {
            devLog("[physics] lastControlsSummary:", summary);
          } else {
            devLog("[physics] debugDump received (no lastControlsSummary)");
          }
        } catch {
          /* intentionally empty */
        }
        break;
      }
      case "error": {
        clearPendingHelloTimeout(worker);
        clearWorkerWatchdog?.(worker);
        const errorMessage = String(msg.payload?.error || msg.error || "Worker error");
        const details = msg.payload?.details || null;
        logger.error("[physics] Worker reported error:", errorMessage);
        addBreadcrumb(
          "physics_worker_reported_error",
          {
            area: "physics",
            action: "worker.error",
            operation: "physics-run",
            phase: "failed",
            engine: get().engine,
            loadMode: get().loadMode,
            mappedBodies: get().mappedBodies,
            totalBodies: get().totalBodies,
            workerFailureDetails: details,
            ...getSceneObservabilitySummary(getSceneState()),
          },
          "error"
        );
        reportRuntimeError(
          new Error(errorMessage),
          {
            source: "physics-worker-reported-error",
          },
          {
            toastKey: "physics-worker-reported-error",
            toastMessage: physicsUserErrorToast(errorMessage),
            toastDetail: physicsErrorToastDetail,
          }
        );
        try {
          worker.terminate();
        } catch (error) {
          logger.warn("[physics] Failed to terminate errored worker", error);
        }
        setPhysicsWorker(null);
        set({
          status: "error",
          lastError: errorMessage,
          lastWorkerFailureDetails: details,
          startupStartedAt: null,
          isRunning: false,
        });
        break;
      }
    }
  };
};
